/**
 * License Validation Chain
 * Orchestrates multiple license validators in priority order:
 * 1. Beta licenses (local, fast)
 * 2. Custom licenses (local, fast)
 * 3. Gumroad licenses (external API, slower)
 */

import type {
  LicenseInfo,
  LicenseValidationResult,
  LicenseActivationResult,
  OfflineGracePeriodStatus,
  DeviceInfo
} from '$lib/types/subscription';

import type {
  LicenseValidator,
  LicenseProgressCallback
} from './interfaces';

import { BetaLicenseValidator } from './BetaLicenseValidator';
import { CustomLicenseValidator } from './CustomLicenseValidator';
import { GumroadLicenseValidator } from './GumroadLicenseValidator';

/**
 * License Validation Chain
 * Implements the Chain of Responsibility pattern for license validation
 */
export class LicenseValidationChain implements LicenseValidator {
  private readonly validators: LicenseValidator[];
  private readonly betaValidator: BetaLicenseValidator;
  private readonly customValidator: CustomLicenseValidator;
  private readonly gumroadValidator: GumroadLicenseValidator;

  constructor(
    betaValidator: BetaLicenseValidator,
    customValidator: CustomLicenseValidator,
    gumroadValidator: GumroadLicenseValidator
  ) {
    this.betaValidator = betaValidator;
    this.customValidator = customValidator;
    this.gumroadValidator = gumroadValidator;

    // Define validation order
    this.validators = [
      betaValidator,
      customValidator,
      gumroadValidator
    ];
  }

  /**
   * Validate a license key using the chain of validators
   */
  async validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
    // Quick check for beta/custom keys to skip unnecessary validators
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.validateLicense(licenseKey);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.validateLicense(licenseKey);
    }

    // Default to Gumroad validator for all other keys
    return await this.gumroadValidator.validateLicense(licenseKey);
  }

  /**
   * Activate a license on this device using appropriate validator
   */
  async activateLicense(
    licenseKey: string,
    deviceInfo: DeviceInfo,
    progressCallback?: LicenseProgressCallback
  ): Promise<LicenseActivationResult> {
    // Route to appropriate validator based on key type
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.activateLicense(licenseKey, deviceInfo, progressCallback);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.activateLicense(licenseKey, deviceInfo, progressCallback);
    }

    // Default to Gumroad
    return await this.gumroadValidator.activateLicense(licenseKey, deviceInfo, progressCallback);
  }

  /**
   * Deactivate license on this device
   */
  async deactivateLicense(licenseKey: string, deviceId?: string): Promise<boolean> {
    // Try to determine which validator owns this license
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.deactivateLicense(licenseKey, deviceId);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.deactivateLicense(licenseKey, deviceId);
    }

    return await this.gumroadValidator.deactivateLicense(licenseKey, deviceId);
  }

  /**
   * Check if license can be validated offline
   */
  async canValidateOffline(licenseKey: string): Promise<boolean> {
    // Beta and custom licenses can always validate offline
    if (BetaLicenseValidator.isBetaKey(licenseKey) || CustomLicenseValidator.isCustomKey(licenseKey)) {
      return true;
    }

    return await this.gumroadValidator.canValidateOffline(licenseKey);
  }

  /**
   * Get offline grace period status
   */
  async getOfflineGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.getOfflineGracePeriodStatus(licenseKey);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.getOfflineGracePeriodStatus(licenseKey);
    }

    return await this.gumroadValidator.getOfflineGracePeriodStatus(licenseKey);
  }

  /**
   * Refresh cached license data
   */
  async refreshLicenseCache(licenseKey: string): Promise<LicenseValidationResult> {
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.refreshLicenseCache(licenseKey);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.refreshLicenseCache(licenseKey);
    }

    return await this.gumroadValidator.refreshLicenseCache(licenseKey);
  }

  /**
   * Get device information for license
   */
  async getDeviceInfo(licenseKey: string): Promise<DeviceInfo[]> {
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.getDeviceInfo(licenseKey);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.getDeviceInfo(licenseKey);
    }

    return await this.gumroadValidator.getDeviceInfo(licenseKey);
  }

  /**
   * Check if device limit allows activation
   */
  async canActivateOnDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return await this.betaValidator.canActivateOnDevice(licenseKey, deviceInfo);
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return await this.customValidator.canActivateOnDevice(licenseKey, deviceInfo);
    }

    return await this.gumroadValidator.canActivateOnDevice(licenseKey, deviceInfo);
  }

  /**
   * Get the appropriate validator for a license key
   * (utility method for internal use)
   */
  private getValidatorForKey(licenseKey: string): LicenseValidator {
    if (BetaLicenseValidator.isBetaKey(licenseKey)) {
      return this.betaValidator;
    }

    if (CustomLicenseValidator.isCustomKey(licenseKey)) {
      return this.customValidator;
    }

    return this.gumroadValidator;
  }
}